import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { PageShell, goldButton } from '../components/PageShell';
import { orderApi } from '../lib/orderApi';

const STEPS = [
  { key: 'paid', label: 'Pago confirmado' },
  { key: 'preparing', label: 'Preparando tu pedido' },
  { key: 'out_for_delivery', label: 'En ruta' },
  { key: 'arriving_soon', label: 'Llegando' },
  { key: 'delivered', label: 'Entregado' },
];
const liveStatuses = ['out_for_delivery', 'arriving_soon'];

const DAY_NAMES_ES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MONTHS_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const formatDeliveryDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const s = `${DAY_NAMES_ES[dt.getDay()]} ${d} de ${MONTHS_ES[m - 1]}`;
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const OrderTracking = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState('');

  const load = useCallback(async () => {
    try { const { data } = await orderApi.get(`/orders/track/${id}`); setOrder(data.order); }
    catch (e) { setError('No se pudo cargar el pedido.'); }
  }, [id]);

  const confirmReceived = useCallback(async () => {
    setConfirming(true);
    setConfirmError('');
    try {
      await orderApi.post(`/orders/track/${id}/confirm`);
      await load();
    } catch (e) {
      setConfirmError('No se pudo confirmar. Inténtalo de nuevo.');
    } finally {
      setConfirming(false);
    }
  }, [id, load]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  const currentIndex = order ? STEPS.findIndex((s) => s.key === order.status) : -1;

  return (
    <PageShell>
      <div className="mx-auto" style={{ maxWidth: '620px' }}>
        <h1 className="font-display" style={{ fontSize: '34px', fontWeight: 700 }}>Seguimiento del pedido</h1>
        {error && <p className="font-body mt-3" style={{ color: '#E0736F' }}>{error}</p>}

        {order && (
          <>
            {order.status === 'arriving_soon' && (
              <div className="mt-6 p-5 text-center" style={{ background: 'rgba(200,40,43,0.12)', border: '1px solid #C8282B' }} data-testid="arriving-banner">
                <p className="font-display" style={{ fontSize: '22px', color: '#F5F5F0' }}>🛵 Tu pedido llega en ~20 minutos</p>
                <p className="font-body mt-1" style={{ fontSize: '13px', color: '#C2C1B6' }}>Nuestro chofer está muy cerca. ¡Prepárate!</p>
              </div>
            )}

            {order.status === 'out_for_delivery' && (
              <div className="mt-6 p-5 text-center" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.35)' }} data-testid="on-way-banner">
                <p className="font-display" style={{ fontSize: '20px', color: '#F5F5F0' }}>🛵 Tu pedido va en camino</p>
                <p className="font-body mt-1" style={{ fontSize: '13px', color: '#C2C1B6' }}>Nuestro chofer salió con tu orden. ¡Ya casi llega!</p>
              </div>
            )}

            <div className="mt-6 p-4" style={{ background: '#141414', border: '1px solid rgba(201,168,76,0.18)' }}>
              <p className="font-body" style={{ fontSize: '12px', color: '#C2C1B6' }}>Pedido #{order.id.slice(0, 8).toUpperCase()}</p>
              <div className="mt-3 space-y-1">
                {order.items.map((it, i) => (
                  <div key={`${it.id}-${i}`} className="flex justify-between font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>
                    <span>{it.qty}× {it.name}{it.modifiers && it.modifiers.length ? ` (${it.modifiers.join(', ')})` : ''}</span>
                    <span style={{ color: '#F5F5F0' }}>${it.line_total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 space-y-1" style={{ borderTop: '1px solid rgba(201,168,76,0.18)' }}>
                {order.subtotal != null && (order.tax_total || 0) > 0 && (
                  <>
                    <div className="flex justify-between font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>
                      <span>Subtotal</span><span style={{ color: '#F5F5F0' }}>${order.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>
                      <span>Impuesto (IVU)</span><span style={{ color: '#F5F5F0' }}>${(order.tax_total || 0).toFixed(2)}</span>
                    </div>
                  </>
                )}
                {(order.tip || 0) > 0 && (
                  <div className="flex justify-between font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>
                    <span>Propina</span><span style={{ color: '#F5F5F0' }}>${(order.tip || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="font-body uppercase" style={{ fontSize: '12px', color: '#C2C1B6' }}>Total</span>
                  <span className="font-bebas" style={{ fontSize: '22px', color: '#C9A84C' }}>${order.total.toFixed(2)}</span>
                </div>
              </div>
              <p className="font-body mt-3" style={{ fontSize: '12px', color: '#C2C1B6' }}>Lugar de entrega: {order.address}</p>
              {order.delivery_date && (
                <p className="font-body mt-1" style={{ fontSize: '13px', color: '#C9A84C' }} data-testid="tracking-delivery-date">
                  Día de entrega: <strong style={{ color: '#F5F5F0' }}>{formatDeliveryDate(order.delivery_date)}</strong>
                </p>
              )}
            </div>

            {order.status === 'cancelled' ? (
              <p className="font-body mt-6" style={{ color: '#E0736F' }}>Este pedido fue cancelado.</p>
            ) : (
              <div className="mt-8 space-y-4">
                {STEPS.map((s, i) => {
                  const done = i <= currentIndex;
                  return (
                    <div key={s.key} className="flex items-center gap-4" data-testid={`step-${s.key}`}>
                      <span style={{ width: '14px', height: '14px', borderRadius: '50%', background: done ? '#C9A84C' : 'transparent', border: `2px solid ${done ? '#C9A84C' : 'rgba(201,168,76,0.35)'}` }} />
                      <span className="font-body" style={{ fontSize: '14px', color: done ? '#F5F5F0' : '#C2C1B6', fontWeight: done ? 500 : 300 }}>{s.label}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Customer delivery confirmation */}
            {order.status !== 'cancelled' && order.status !== 'delivered' && (
              <div className="mt-8 p-5 text-center" style={{ background: '#141414', border: '1px solid rgba(201,168,76,0.3)' }} data-testid="confirm-section">
                <p className="font-body" style={{ fontSize: '13px', color: '#C2C1B6', marginBottom: '12px' }}>
                  ¿Ya recibiste tu pedido? Confírmalo aquí.
                </p>
                <button
                  onClick={confirmReceived}
                  disabled={confirming}
                  style={{ ...goldButton, opacity: confirming ? 0.6 : 1, cursor: confirming ? 'not-allowed' : 'pointer' }}
                  data-testid="confirm-received-btn"
                >
                  {confirming ? 'Confirmando…' : 'Confirmar que recibí mi pedido'}
                </button>
                {confirmError && <p className="font-body mt-3" style={{ fontSize: '13px', color: '#E0736F' }}>{confirmError}</p>}
              </div>
            )}

            {order.status === 'delivered' && (
              <div className="mt-8 p-6 text-center" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid #C9A84C' }} data-testid="delivered-message">
                <p className="font-display" style={{ fontSize: '24px', color: '#C9A84C', fontWeight: 700 }}>¡Buen provecho!</p>
                <p className="font-body mt-2" style={{ fontSize: '14px', color: '#F5F5F0' }}>
                  Disfruta el mejor ramen de la montaña. ¡Gracias por tu orden!
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </PageShell>
  );
};

export default OrderTracking;
